/**
 * Chart components for the credem.client_brief v2 artifact.
 *
 * Each section in the brief may include an optional `chart` field; this
 * module ships one component per chart type and a `BriefChart` switch
 * that dispatches on `chart.type`.
 *
 * Types supported (kept in sync with src/credem_explorer/client_brief.py):
 *   - donut         → composition asset allocation
 *   - bar           → performance per horizon (with positive/negative coloring)
 *   - grouped_bar   → concentration_drift (actual vs target per asset class)
 *
 * The backend may still emit `timeline` chart payloads (events,
 * flows_maturities). They are intentionally dropped here: `parseChart`
 * returns null so the section renders its row data without a chart.
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Sector,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { PieSectorDataItem } from 'recharts/types/polar/Pie'

import styles from './BriefCharts.module.scss'
import { colorForLabel } from '../app/components/charts/colors'
import { cn } from '@/lib/utils'

// =====================================================================
// Chart spec types — mirror the server-side dataclasses
// =====================================================================

export interface BriefChartDonut {
  type: 'donut'
  labels: string[]
  values: number[]
  values_eur?: number[]
  title?: string
}

export interface BriefChartBar {
  type: 'bar'
  x: string[]
  y_pct?: (number | null)[]
  y_eur?: (number | null)[]
  highlight_negative?: boolean
  title?: string
}

export interface BriefChartGroupedBar {
  type: 'grouped_bar'
  x: string[]
  actual: number[]
  target: (number | null)[]
  in_band: boolean[]
  title?: string
  target_label?: string
}

export type BriefChartSpec =
  | BriefChartDonut
  | BriefChartBar
  | BriefChartGroupedBar

// =====================================================================
// Formatters — Italian locale
// =====================================================================

function formatEurCompact(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return 'n.d.'
  const abs = Math.abs(amount)
  if (abs >= 1_000_000) {
    return `€${(amount / 1_000_000).toFixed(2).replace('.', ',')}M`
  }
  if (abs >= 1_000) {
    return `€${(amount / 1_000).toFixed(1).replace('.', ',')}k`
  }
  const intval = Math.round(amount)
  return `€${intval.toLocaleString('it-IT')}`
}

function formatPct(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return 'n.d.'
  const p = value * 100
  const sign = p > 0 ? '+' : p < 0 ? '-' : ''
  return `${sign}${Math.abs(p).toFixed(decimals).replace('.', ',')}%`
}

const DONUT_EXPAND_PX = 8
const DONUT_ANIM_MS = 400

// =====================================================================
// Donut — composition asset allocation
// =====================================================================

function BriefDonut({ chart }: { chart: BriefChartDonut }) {
  const items = chart.labels.map((label, idx) => ({
    label,
    pct: chart.values[idx] ?? 0,
    eur: chart.values_eur?.[idx] ?? null,
    color: colorForLabel(label, idx),
  }))
  const totalPct = items.reduce((s, x) => s + x.pct, 0)
  const totalEur = items.reduce((s, x) => s + (x.eur ?? 0), 0)

  // Two-tier active state:
  //   * hoverIndex is set/cleared by mouse enter/leave (chart OR list).
  //   * selectedIndex is toggled by clicks (chart OR list) and persists
  //     until the user clicks the same item again, or clicks a different
  //     item to switch.
  //
  // The visual `activeIndex` is `hoverIndex ?? selectedIndex` — hover wins
  // while the pointer is on a target, otherwise the pinned selection paints.
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const activeIndex = hoverIndex !== null ? hoverIndex : selectedIndex

  // `displayActiveIndex` tracks which slice the active-shape (expanded
  // sector) is animating. It diverges from `activeIndex` during the shrink
  // animation so the sector can smoothly retract before disappearing.
  const [displayActiveIndex, setDisplayActiveIndex] = useState<number | null>(null)
  const [expandOffset, setExpandOffsetState] = useState(0)
  const expandOffsetRef = useRef(0)
  const displayActiveIndexRef = useRef<number | null>(null)
  const rafRef = useRef<number | undefined>(undefined)

  const runAnimation = useCallback((to: number, onDone?: () => void) => {
    if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current)
    const from = expandOffsetRef.current
    let t0: number | undefined

    const tick = (now: number) => {
      if (t0 === undefined) t0 = now
      const t = Math.min((now - t0) / DONUT_ANIM_MS, 1)
      const eased = 1 - (1 - t) ** 3
      const next = from + (to - from) * eased
      expandOffsetRef.current = next
      setExpandOffsetState(next)
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        onDone?.()
      }
    }

    rafRef.current = requestAnimationFrame(tick)
  }, [])

  // Drive the active-shape animation from a single index. Passing `null`
  // shrinks the current active shape back; passing an index expands that
  // slice (resetting the offset first if switching from a different one).
  const animateTo = useCallback(
    (next: number | null) => {
      if (next === null) {
        runAnimation(0, () => {
          displayActiveIndexRef.current = null
          setDisplayActiveIndex(null)
        })
        return
      }
      if (displayActiveIndexRef.current !== next) {
        expandOffsetRef.current = 0
        setExpandOffsetState(0)
      }
      displayActiveIndexRef.current = next
      setDisplayActiveIndex(next)
      runAnimation(DONUT_EXPAND_PX)
    },
    [runAnimation],
  )

  const handleEnter = useCallback(
    (index: number) => {
      setHoverIndex(index)
      animateTo(index)
    },
    [animateTo],
  )

  const handleLeave = useCallback(() => {
    setHoverIndex(null)
    // Fall back to the pinned slice (if any), otherwise shrink back.
    animateTo(selectedIndex)
  }, [animateTo, selectedIndex])

  const handleClick = useCallback((index: number) => {
    setSelectedIndex((current) => (current === index ? null : index))
  }, [])

  // Recharts passes `(payload, index)` — we only need the index.
  const handlePieEnter = useCallback(
    (_data: unknown, index: number) => handleEnter(index),
    [handleEnter],
  )
  const handlePieClick = useCallback(
    (_data: unknown, index: number) => handleClick(index),
    [handleClick],
  )

  // Click-outside-to-deselect. Listen to document `mousedown` and clear the
  // pinned selection if the click lands outside the chart+list container.
  // Hover state is unaffected; this only targets the persistent click pin.
  const rootRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (selectedIndex === null) return undefined
    const onDocMouseDown = (event: MouseEvent) => {
      const root = rootRef.current
      if (!root) return
      if (event.target instanceof Node && root.contains(event.target)) return
      setSelectedIndex(null)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [selectedIndex])

  if (items.length === 0 || totalPct <= 0) return null

  // Always pass `activeIndex` as a controlled value — using `undefined` flips
  // Recharts back to its internal mouse-tracking mode, after which external
  // updates (e.g. list hover) don't get reflected reliably. An empty array
  // keeps the prop controlled with "no active sector".
  const pieActiveIndex: number | number[] =
    displayActiveIndex !== null ? displayActiveIndex : []

  return (
    <div ref={rootRef} className={styles.allocationComparison}>
      <div className={styles.allocationBody}>
        <div
          className={styles.allocationDonut}
          aria-label={`Allocazione su ${items.length} asset class`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={items}
                dataKey="pct"
                nameKey="label"
                innerRadius="60%"
                outerRadius="100%"
                startAngle={90}
                endAngle={-270}
                strokeWidth={0}
                isAnimationActive={false}
                activeIndex={pieActiveIndex}
                activeShape={(props: PieSectorDataItem) => (
                  <Sector
                    {...props}
                    outerRadius={Number(props.outerRadius) + expandOffset}
                    innerRadius={Number(props.innerRadius)}
                  />
                )}
                onMouseEnter={handlePieEnter}
                onMouseLeave={handleLeave}
                onClick={handlePieClick}
              >
                {items.map((item, index) => (
                  <Cell
                    key={item.label}
                    fill={item.color}
                    opacity={activeIndex === null || activeIndex === index ? 1 : 0.35}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className={styles.donutCenter} aria-hidden="true">
            <strong>{formatEurCompact(totalEur)}</strong>
            <span>totale</span>
          </div>
        </div>
        <ul className={styles.allocationList}>
          {items.map((item, index) => (
            <li
              key={item.label}
              className={cn(
                styles.allocationRow,
                styles.donutAllocationRow,
                activeIndex === index && styles.allocationRowActive,
                activeIndex !== null &&
                  activeIndex !== index &&
                  styles.allocationRowDimmed,
              )}
              onClick={() => handleClick(index)}
            >
              <span className={styles.allocationSwatch} aria-hidden>
                <span
                  style={{ background: item.color }}
                  className={styles.allocationSwatchDot}
                />
              </span>
              <span className={styles.allocationLabel}>{item.label}</span>
              <span className={styles.allocationValue}>
                {formatPct(item.pct, 1)}
              </span>
              <span className={styles.donutAllocationEur}>
                {item.eur !== null ? formatEurCompact(item.eur) : 'n.d.'}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// =====================================================================
// Performance bar — % per horizon with positive/negative coloring
// =====================================================================

const POSITIVE_FILL = '#3fb95a' // green
const NEGATIVE_FILL = '#d34c4c' // red
const NEUTRAL_FILL = '#8a8a8a'

function BriefBar({ chart }: { chart: BriefChartBar }) {
  const data = chart.x.map((label, idx) => ({
    label,
    pct: chart.y_pct?.[idx] ?? 0,
    eur: chart.y_eur?.[idx] ?? null,
  }))
  if (data.length === 0) return null

  return (
    <div className={styles.bar}>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 10, right: 16, left: 8, bottom: 8 }}>
          <CartesianGrid stroke="var(--border)" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
          <YAxis
            tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
            tick={{ fontSize: 12 }}
            width={50}
          />
          <ReferenceLine y={0} stroke="var(--border)" />
          <Tooltip
            formatter={(value: number, _name, item) => {
              const eur = (item?.payload as { eur?: number | null } | undefined)?.eur ?? null
              return [
                `${formatPct(value, 2)}${eur !== null ? ` · ${formatEurCompact(eur)}` : ''}`,
                'Performance',
              ]
            }}
            labelFormatter={(label: string) => label}
            cursor={{ fill: 'var(--bank-hover-readable-bg)' }}
            contentStyle={{
              background: 'var(--card)',
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}
          />
          <Bar dataKey="pct" radius={[4, 4, 0, 0]}>
            {data.map((d, idx) => {
              const fill =
                d.pct > 0
                  ? POSITIVE_FILL
                  : d.pct < 0
                    ? NEGATIVE_FILL
                    : NEUTRAL_FILL
              return <Cell key={idx} fill={fill} />
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

// =====================================================================
// Grouped bar — actual vs target per asset class (drift)
// =====================================================================

function BriefGroupedBar({ chart }: { chart: BriefChartGroupedBar }) {
  const data = chart.x.map((label, idx) => ({
    label,
    actual: (chart.actual?.[idx] ?? 0) * 100,
    target: chart.target?.[idx] == null ? null : (chart.target[idx] ?? 0) * 100,
    inBand: chart.in_band?.[idx] ?? false,
  }))
  if (data.length === 0) return null

  // Bank rule: the target shown is the TACTICAL optimal (TAA), not SAA. The
  // backend supplies target_label; fall back to a clear default rather than the
  // old hardcoded "Target SAA".
  const targetLabel = chart.target_label ?? 'Ottimale (TAA)'

  return (
    <div className={styles.groupedBar}>
      <ResponsiveContainer width="100%" height={Math.max(180, 32 * data.length + 60)}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 8, right: 24, left: 12, bottom: 8 }}
        >
          <CartesianGrid stroke="var(--border)" horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={(v: number) => `${v.toFixed(0)}%`}
            tick={{ fontSize: 12 }}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fontSize: 12 }}
            width={140}
          />
          <Tooltip
            formatter={(value: number, name: string) => [
              `${value.toFixed(1).replace('.', ',')}%`,
              name === 'actual' ? 'Attuale' : targetLabel,
            ]}
            cursor={{ fill: 'var(--bank-hover-readable-bg)' }}
            contentStyle={{
              background: 'var(--card)',
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}
          />
          <Legend
            formatter={(value: string) =>
              value === 'actual' ? 'Attuale' : targetLabel
            }
            wrapperStyle={{ fontSize: 12 }}
          />
          <Bar dataKey="actual" fill="#5b8def" radius={[0, 3, 3, 0]}>
            {data.map((d, idx) => (
              <Cell key={idx} fill={d.inBand ? '#5b8def' : '#f0a050'} />
            ))}
          </Bar>
          <Bar dataKey="target" fill="#cccccc" radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

// =====================================================================
// Dispatcher
// =====================================================================

export function BriefChart({ chart }: { chart: BriefChartSpec | null }) {
  if (!chart) return null
  switch (chart.type) {
    case 'donut':
      return <BriefDonut chart={chart} />
    case 'bar':
      return <BriefBar chart={chart} />
    case 'grouped_bar':
      return <BriefGroupedBar chart={chart} />
    default:
      return null
  }
}

// =====================================================================
// Parser — extracts a BriefChartSpec from the section JSON
// =====================================================================

function parseStringList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw.map((v) => (typeof v === 'string' ? v : '')).filter(Boolean)
}

function parseNumberList(raw: unknown): (number | null)[] {
  if (!Array.isArray(raw)) return []
  return raw.map((v) => {
    if (typeof v === 'number' && !Number.isNaN(v)) return v
    if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) {
      return Number(v)
    }
    return null
  })
}

function parseBoolList(raw: unknown): boolean[] {
  if (!Array.isArray(raw)) return []
  return raw.map((v) => Boolean(v))
}

export function parseChart(raw: unknown): BriefChartSpec | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  const type = typeof obj.type === 'string' ? obj.type : ''
  const title = typeof obj.title === 'string' ? obj.title : undefined

  if (type === 'donut') {
    const labels = parseStringList(obj.labels)
    const valuesRaw = parseNumberList(obj.values)
    const valuesEurRaw = parseNumberList(obj.values_eur)
    const values = valuesRaw.map((v) => v ?? 0)
    if (labels.length !== values.length || labels.length === 0) return null
    return {
      type: 'donut',
      labels,
      values,
      values_eur: valuesEurRaw.map((v) => v ?? 0),
      title,
    }
  }

  if (type === 'bar') {
    const x = parseStringList(obj.x)
    if (x.length === 0) return null
    return {
      type: 'bar',
      x,
      y_pct: parseNumberList(obj.y_pct),
      y_eur: parseNumberList(obj.y_eur),
      highlight_negative: Boolean(obj.highlight_negative),
      title,
    }
  }

  // `timeline` is intentionally not rendered anymore — the backend may still
  // emit it for events / flows_maturities sections, but we drop the payload
  // so the section falls back to its row data without a chart.
  if (type === 'timeline') return null

  if (type === 'grouped_bar') {
    const x = parseStringList(obj.x)
    const actual = parseNumberList(obj.actual).map((v) => v ?? 0)
    const target = parseNumberList(obj.target)
    const in_band = parseBoolList(obj.in_band)
    if (x.length === 0 || x.length !== actual.length) return null
    const target_label = typeof obj.target_label === 'string' ? obj.target_label : undefined
    return { type: 'grouped_bar', x, actual, target, in_band, title, target_label }
  }

  return null
}
